import { HeadingItem, LicensePlateCheckbox, Notify, VerifyItemCatchWeight, VerifyItemDetails } from 'components';
import { BarcodeSearch } from 'components/barcode';
import { ItemQuantityCard } from 'components/verify-item';
import { TripContext } from 'contexts';
import { ScrollView } from 'devextreme-react/scroll-view';
import notify from 'devextreme/ui/notify';
import React, { Fragment, useContext, useEffect, useState } from 'react';
import { useNavigation, useOrderItemService, useParamService, useSettingService } from 'services';
import { ReconcileReasonType, VerifiedMethod } from 'services/enums';
import { CatchWeight, LicensePlate, LicensePlateItem, LineItem, Order, OrderItem, OrderItemChange, Stop } from 'services/types';
import { ConfirmationPopup } from 'components/pop-up';
import { faThumbsUp } from '@fortawesome/pro-solid-svg-icons';
import soundFile from 'resources/unknownBarcodeSound.mp3';

export const VerifyItemPage = () => {

    const { getIntValue, getStringValue } = useParamService
    const { navigateTo } = useNavigation()
    const { updateOrderItemChange, verifyItems, unVerifyItems, resetOrderItemChangeApproval, getOtherDeliveriesWithItem } = useOrderItemService()
    const { getTrainingMode } = useSettingService()

    const isTrainingMode: boolean = getTrainingMode()
    const tripContext = useContext(TripContext)

    const getStopId = (): number | undefined => getIntValue('stopId')
    const getItemId = (): number | undefined => getIntValue('itemId')
    const verifiedMethod: string | undefined = getStringValue('verifiedMethod')

    const [isReconcilingCatchWeight, setIsReconcilingCatchWeight] = useState(false);
    const [ displayHazmatNotification, setDisplayHazmatNotification ] = useState<boolean>(false)
    const [showConfirmPopup, setConfirmPopupVisible] = useState<boolean>(true)

    const [otherDeliveriesWithItem, setOtherDeliveriesWithItem] = useState<string[]>([])
    const audio = new Audio(soundFile)

    const stopId: number | undefined = getStopId()
    const itemId: number | undefined = getItemId()
    const stop: Stop = tripContext.stops.find(s => s.id == stopId) ?? {} as Stop
    const tripId: number | undefined = stop?.tripId
    const orders: Order[] = tripContext.orders.filter(o => o.stopId == stop.id)
    const licensePlates: LicensePlate[] = tripContext.licensePlates.filter(lp => lp.stopId == stopId)
    const lineItem: LineItem = tripContext.lineItems.find(li => li.id == itemId) ?? {} as LineItem
    const lpItems: LicensePlateItem[] = tripContext.licensePlateItems.filter(lpi => lpi.lineItemId === lineItem.id && licensePlates.some(lp => lp.id === lpi.licensePlateId))
    
    useEffect(() => {
        if (isTrainingMode) {
            notify(`Scan your next item to continue`, 'success', 3000)
        }
        if(lineItem.isHazMat){
            setDisplayHazmatNotification(true)
        }
        const otherDeliveries = getOtherDeliveriesWithItem(stop.id, tripId, lineItem.id)
        setOtherDeliveriesWithItem(otherDeliveries)
        if(otherDeliveries.length > 0) {
            audio.play()
        }
    }, [])

    const isItemOnMultipleDeliveries: boolean = otherDeliveriesWithItem?.length > 0

    const getItems = (): OrderItem[] => {
        let items: OrderItem[] = []
        orders.forEach(order => {
            items = [...items, ...tripContext.orderItems.filter(oi => oi.orderId == order.id && oi.lineItemId == itemId)]
        })

        return items
    }

    const getCatchWeightItems = (): CatchWeight[] => {
        let catchWeightItems: CatchWeight[] = []
        getItems()?.forEach(orderItem => {
            catchWeightItems = [...catchWeightItems, ...tripContext.catchWeights.filter(cw => cw.orderItemId == orderItem.id)]
        })
        return catchWeightItems
    }

    const areAllCatchWeightsVerified = getCatchWeightItems().every(x => x.isVerified)

    useEffect(() => {
        if (lpItems.length === 1 && areAllCatchWeightsVerified) {
            selectLp(lpItems[0])
        }
    }, [itemId])
    
    const getItemChanges = (): OrderItemChange[] => {
        let changes: OrderItemChange[] = []
        getItems()?.forEach(orderItem => {
            changes = [...changes, ...tripContext.orderItemChanges.filter(oi => oi.orderItemId == orderItem.id)]
        })
        return changes
    }

    const getLicensePlateItems = () => {
        let lpItems: LicensePlateItem[] = []
        licensePlates.forEach(lp => {
            lpItems = [...lpItems, ...tripContext.licensePlateItems.filter(lpi => lpi.licensePlateId == lp.id && lpi.lineItemId == lineItem.id)]
        })
    }

    useEffect(() => {
        if (tripContext.licensePlateItems.length > 0 && tripContext.licensePlates.length > 0) {
            getLicensePlateItems()
        }
    }, [lineItem])

    const onCatchWeightUnverified = (): void => {
        lpItems.every(lpItem => selectLp(lpItem, false))
    }

    const selectLp = (lpItem: LicensePlateItem, isConfirmed: boolean = true): void => {
        //only update item if user is still working on it
        if(getItemId() === lpItem.lineItemId) {
            tripContext.licensePlateItems.put({ ...lpItem, isVerified: isConfirmed })
            let lpItems: LicensePlateItem[] = []
            const lineItem: LineItem = tripContext.lineItems.find(li => li.id == lpItem.lineItemId) ?? {} as LineItem
            licensePlates.forEach(lp => {
                lpItems = [...lpItems, ...tripContext.licensePlateItems.filter(lpi => lpi.licensePlateId == lp.id && lpi.lineItemId == lineItem.id)]
            })
    
            const items: OrderItem[] = getItems()
            let isLastLp: boolean = lpItems.filter(lpi => lpi.isVerified == undefined || lpi.isVerified == false).length == 0
            if(isLastLp && items[0].verifiedMethod == null) {
                verifiedMethod == VerifiedMethod.Scanned ? verifyItems(items, VerifiedMethod.Scanned) : verifyItems(items, VerifiedMethod.Manual)
            }
            if(!isLastLp && items[0].verifiedMethod != null) {
                unVerifyItems(items)
            }
        }
        
    }

    const handleItemQuantityChanged = async (orderItemId: string | null, verifiedQuantity: number): Promise<void> => {
        const item: OrderItem = tripContext.orderItems.find(item => item.id.toString() == orderItemId) ?? {} as OrderItem
        const changes: OrderItemChange[] = tripContext.orderItemChanges.filter(cw => cw.orderItemId.toString() == orderItemId) ?? {} as OrderItemChange
        const difference: number = verifiedQuantity - item.shippedQuantity
        const changeIndex: number | undefined = changes.findIndex(change => change.orderItemId == item.id && (change.reconcileReasonId === ReconcileReasonType.Shortage || change.reconcileReasonId === ReconcileReasonType.OverageKept))
        let change: OrderItemChange | null = (changeIndex != null && changeIndex >= 0) ? (changes && changes[changeIndex]) : null

        if (change == null) {
            change = {
                id: new Date().getTime(),
                orderItemId: item.id,
                quantity: Math.abs(difference),
                reconcileReasonId: difference > 0 ? ReconcileReasonType.OverageKept : ReconcileReasonType.Shortage,
                refusalChoiceId: null,
                refusalComment: null,
                returnSticker: null,
                noReturnStickerReason: null,
                codeDate: null,
                isChangeApproved: false
            }
            changes.push(change)
        }
        else {
            changes[changeIndex].quantity = Math.abs(difference)
            changes[changeIndex].reconcileReasonId = difference > 0 ? ReconcileReasonType.OverageKept : ReconcileReasonType.Shortage
            resetOrderItemChangeApproval(item)
        }

        item.countedQuantity = verifiedQuantity ?? 0

        tripContext.orderItems.put({ ...item, countedQuantity: verifiedQuantity })
        updateOrderItemChange(change);
        setConfirmPopupVisible(true);
    }

    let areItemsCountedQuantityZeroAndScanned = () => {
        for (var item of getItems()) {
            if(item.countedQuantity > 0)
                return false;

            if (item.verifiedMethod !== VerifiedMethod.Scanned) {
                return false;
            }
       }
        
        return true;
    }

    return (
        <>
            <Fragment>
                <Notify isVisible={displayHazmatNotification} message={'Caution! Hazardous Material'} onLeftSideIconClick={() => { setDisplayHazmatNotification(false) }} displayRightSideIcon={false}></Notify>
            </Fragment>
            <ScrollView style={{ paddingBottom: "50px" }}>
                <div>
                    <BarcodeSearch tripId={tripId} stopId={stopId} additionalInvalidBarcodeCriteria={() => !isReconcilingCatchWeight} />
                    <VerifyItemDetails stopId={stopId} tripId={tripId} lineItemId={itemId} />
                    <div className={'card-material'}>
                        <HeadingItem text={'LP Quantities'} />
                        <Fragment>
                            {lpItems.map(lp => <LicensePlateCheckbox key={lp.licensePlateId} lpItem={lp} isCheckBoxDisabled={!areAllCatchWeightsVerified} onLpSelected={(lpItem: LicensePlateItem, isChecked: boolean) => selectLp(lpItem, isChecked)} />)}
                        </Fragment>
                    </div>
                    {getCatchWeightItems()?.length > 0 ?
                        <VerifyItemCatchWeight stopId={stopId} lineItemId={lineItem.id} onReconcilingStarted={() => setIsReconcilingCatchWeight(true)} onReconcilingCompleted={() => setIsReconcilingCatchWeight(false)} onCatchWeightUnverified={() => onCatchWeightUnverified()} />
                        :
                        <div>
                            {getItems()?.map(item =>
                                <ItemQuantityCard
                                    key={`${item.customerPoNumber}${item.id}`}
                                    itemId={item.id}
                                    onQuantityAdjusted={handleItemQuantityChanged}
                                    onClick={(title) => navigateTo(`add-refusal?orderItemId=${item.id}`, title)}
                                />
                            )}
                        </div>
                    }

                    <ConfirmationPopup
                        isVisible={showConfirmPopup && areItemsCountedQuantityZeroAndScanned()}
                        title="Warning"
                        message={`This item was scanned but the counted quantity has been set to zero`}
                        button1={{ icon: faThumbsUp, text: "OK" }}
                        onClickButton1={() => setConfirmPopupVisible(false)}
                        isCloseButtonVisible={true}
                        onClickCloseButton={() => setConfirmPopupVisible(false)}
                    />

                </div>

                     <ConfirmationPopup
                        isVisible={showConfirmPopup && isItemOnMultipleDeliveries}
                        title="Multiple Customer Alert"
                        width={300}
                        height={350}
                        render={() =>
                            <div>
                                <div className='confirmation-popup-header'>
                                    This item also on customer(s):
                                </div>
                                <div className='confirmation-popup-content'>
                                    {otherDeliveriesWithItem.map((customer, index) => 
                                        <li className='red-list' key={index}>{customer}</li>
                                    )}
                                </div>
                            </div>
                        }
                        button1={{ icon: faThumbsUp, text: "OK" }}
                        onClickButton1={() => { setConfirmPopupVisible(false)}}
                        isCloseButtonVisible={true}
                        onClickCloseButton={() => { setConfirmPopupVisible(false)}}
                    />
                
            </ScrollView>
        </>
        
    )
}